import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import LogoBarberia from '../../assets/LogoBarbearia.png';
import { db, auth, functions } from '../../services/firebaseConnection';
import { collection, getDoc, getDocs, deleteField, query, updateDoc, doc, deleteDoc, setDoc, addDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import './homeBarbe.css';
import { FaCalendarAlt, FaTools, FaArrowLeft, FaBars, FaClock, FaEdit, FaUsers, FaChartPie } from 'react-icons/fa';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import DashboardClientes from './DashboardClientes';
const GENERAL_SPECIAL_DAY_PROFESSIONAL = '__general__';
// Padrões dos avisos ao dono (configuracoes/notificacoes.events) — valem quando o doc/campo não existe
const DEFAULT_NOTIFICATION_EVENTS = {
  novoAgendamento: true,
  remarcacao: true,
  horarioLiberado: false,
  resumoDiario: true,
};
const NOTIFICATION_EVENT_LABELS = [
  { key: 'novoAgendamento', label: 'Novo agendamento confirmado' },
  { key: 'remarcacao', label: 'Remarcação' },
  { key: 'horarioLiberado', label: 'Horário liberado' },
  { key: 'resumoDiario', label: 'Resumo da agenda às 07:00' },
];
const normalizeString = (value) =>
  value
    ? value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    : '';
const resolvePhoneNumber = (appointment, matchedUser) => {
  const pickFirstFilled = (candidates) => {
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
      if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
        return candidate.toString();
      }
    }
    return '';
  };
  return pickFirstFilled([
    appointment?.phone,
    appointment?.phoneNumber,
    appointment?.telefone,
    appointment?.contactPhone,
    appointment?.contact?.phone,
    appointment?.pagador?.telefone,
    appointment?.pagador?.phone,
    appointment?.pagador?.phoneNumber,
    matchedUser?.phone,
    matchedUser?.phoneNumber,
    matchedUser?.telefone,
    matchedUser?.whatsapp,
    matchedUser?.whatsApp,
  ]);
};
export default function HomeBarbe() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); 
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState({});
  const [availableTimes, setAvailableTimes] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [businessHours, setBusinessHours] = useState({});
  const [users, setUsers] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [specialDayProfessional, setSpecialDayProfessional] = useState(GENERAL_SPECIAL_DAY_PROFESSIONAL);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentProfessionalName, setCurrentProfessionalName] = useState(null);
  const [currentProfessionalId, setCurrentProfessionalId] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showBarbers, setShowBarbers] = useState(false);
  // Agenda no WhatsApp/Telegram: username do bot (null = desconhecido/não configurado)
  const [telegramBotUsername, setTelegramBotUsername] = useState(null);
  // Status dos canais de envio (null = ainda consultando)
  const [notifChannels, setNotifChannels] = useState({ telegram: null, whatsapp: null });
  const telegramBotInfoRequestedRef = useRef(false);
  // Avisos para o dono (doc configuracoes/notificacoes)
  const [notifConfig, setNotifConfig] = useState({
    ownerWhatsapp: '',
    ownerTelegramChatId: '',
    events: { ...DEFAULT_NOTIFICATION_EVENTS },
  });
  const [savingNotifConfig, setSavingNotifConfig] = useState(false);
  const fullDayTimeOptions = useMemo(
    () => Array.from({ length: 24 }, (_, hour) => `${hour.toString().padStart(2, '0')}:00`),
    []
  );
  // Cache em tempo real da coleção 'agendamentos': baixa uma vez e recebe só as mudanças.
  // Evita baixar a coleção inteira a cada troca de data (causa dos travamentos).
  const [appointmentDocs, setAppointmentDocs] = useState(null);
  const appointmentDocsRef = useRef(null);
  // Se o listener morrer (ex.: erro de permissão), volta a buscar direto do servidor
  const appointmentListenerDeadRef = useRef(false);
  // Última busca feita (painel, modal de edição ou dia especial) — alvo dos refreshes em tempo real
  const lastFetchRef = useRef(null);
  // Sequência para descartar respostas de buscas antigas que resolvem fora de ordem
  const fetchSeqRef = useRef(0);
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'agendamentos'),
      (snapshot) => {
        const docsMap = new Map();
        snapshot.forEach((docSnap) => docsMap.set(docSnap.id, docSnap.data()));
        appointmentDocsRef.current = docsMap;
        appointmentListenerDeadRef.current = false;
        setAppointmentDocs(docsMap);
      },
      (error) => {
        console.error('Erro ao ouvir agendamentos:', error);
        // Listener morto não recebe mais nada: invalida o cache para as próximas
        // buscas voltarem a ler direto do servidor (comportamento antigo)
        appointmentListenerDeadRef.current = true;
        appointmentDocsRef.current = null;
      }
    );
    return () => unsubscribe();
  }, []);
  const userDirectory = useMemo(() => {
    const directory = new Map();
    users.forEach((user) => {
      if (user?.id) {
        directory.set(user.id, user);
      }
    });
    return directory;
  }, [users]);
  const loadProfessionals = useCallback(async () => {
    const snapshot = await getDocs(collection(db, 'profissionais'));
    const professionalsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setProfessionals(professionalsList);
    return professionalsList;
  }, []);
  const findProfessionalMatch = useCallback((professionalsList, candidate) => {
    const normalizedCandidate = normalizeString(candidate);
    if (!normalizedCandidate) {
      return null;
    }
    return (
      professionalsList.find((professional) => {
        const normalizedProfessionalName = normalizeString(professional?.name);
        return (
          normalizedProfessionalName === normalizedCandidate ||
          normalizedProfessionalName.includes(normalizedCandidate) ||
          normalizedCandidate.includes(normalizedProfessionalName)
        );
      }) || null
    );
  }, []);
  useEffect(() => {
    const authInstance = getAuth();
    const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
      if (!user) {
        setIsAdmin(false);
        setCurrentProfessionalName(null);
        setCurrentProfessionalId(null);
        setAuthReady(true);
        navigate('/loginBarbe');
        return;
      }
      const isMainAccount = user.uid === 'GrxyhF6Bptcvyl2rCbFy8FWoTtk1';
      setIsAdmin(isMainAccount);
      if (isMainAccount) {
        setCurrentProfessionalName(null);
        setCurrentProfessionalId(null);
        setAuthReady(true);
        return;
      }
      try {
        const professionalsList = await loadProfessionals();
        const normalizedUserEmail = normalizeString(user.email);
        let resolvedProfessional = null;
        if (normalizedUserEmail) {
          resolvedProfessional =
            professionalsList.find((professional) => {
              const professionalEmail =
                professional?.email ||
                professional?.accountEmail ||
                professional?.contactEmail ||
                professional?.loginEmail;
              return normalizeString(professionalEmail) === normalizedUserEmail;
            }) || null;
        }
        const ownerDocRef = doc(db, 'propietario', user.uid);
        const ownerDoc = await getDoc(ownerDocRef);
        const ownerData = ownerDoc.exists() ? ownerDoc.data() : null;
        const storedProfessionalId = ownerData?.professionalId || ownerData?.assignedProfessionalId;
        if (!resolvedProfessional && storedProfessionalId) {
          resolvedProfessional =
            professionalsList.find((professional) => professional.id === storedProfessionalId) || null;
        }
        const candidateNames = [];
        if (ownerData) {
          if (ownerData.professionalName) {
            candidateNames.push(ownerData.professionalName);
          }
          if (ownerData.professional) {
            candidateNames.push(ownerData.professional);
          }
          if (ownerData.name) {
            candidateNames.push(ownerData.name);
          }
          if (ownerData.displayName) {
            candidateNames.push(ownerData.displayName);
          }
        }
        if (!resolvedProfessional) {
          for (const candidate of candidateNames) {
            const match = findProfessionalMatch(professionalsList, candidate);
            if (match) {
              resolvedProfessional = match;
              break;
            }
          }
        }
        if (!resolvedProfessional && user.displayName) {
          resolvedProfessional = findProfessionalMatch(professionalsList, user.displayName);
        }
        if (!resolvedProfessional && normalizedUserEmail) {
          const emailPrefix = normalizedUserEmail.split('@')[0];
          resolvedProfessional = findProfessionalMatch(professionalsList, emailPrefix);
        }
        if (resolvedProfessional) {
          setCurrentProfessionalName(resolvedProfessional.name || null);
          setCurrentProfessionalId(resolvedProfessional.id || null);
          if (
            !ownerData ||
            ownerData.professionalId !== resolvedProfessional.id ||
            ownerData.professionalName !== resolvedProfessional.name
          ) {
            await setDoc(
              ownerDocRef,
              {
                professionalId: resolvedProfessional.id,
                professionalName: resolvedProfessional.name,
              },
              { merge: true }
            );
          }
        } else {
          setCurrentProfessionalName(null);
          setCurrentProfessionalId(null);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do profissional:', error);
        setCurrentProfessionalName(null);
        setCurrentProfessionalId(null);
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsubscribe();
  }, [navigate, loadProfessionals, findProfessionalMatch]);
  useEffect(() => {
    const fetchBusinessHours = async () => {
      const querySnapshot = await getDocs(collection(db, "businessHours"));
      const hoursData = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Incluir o ID do documento junto com os dados
        hoursData[data.day] = { id: doc.id, open: data.open, close: data.close, semana: data.semana };
      });
      setBusinessHours(hoursData);
    };
    fetchBusinessHours();
  }, []);
  useEffect(() => {
    loadProfessionals().catch((error) => {
      console.error('Erro ao buscar lista de profissionais:', error);
    });
  }, [loadProfessionals]);
  const isTimeSlotAvailable = async (date, time, professional) => {
    const appointmentsPath = `agendamentos`;
    const appointmentsRef = collection(db, appointmentsPath);
    const querySnapshot = await getDocs(query(appointmentsRef));
    let isAvailable = true;
    querySnapshot.forEach((doc) => {
      const userAppointments = doc.data()[date] || [];
      userAppointments.forEach((appointment) => {
        if (appointment.hora === time && appointment.professional === professional) {
          isAvailable = false;
        }
      });
    });
    return isAvailable; // Retorna true se o horário estiver disponível
  };
  // Função para abrir o modal de edição
  const handleEditAppointment = (appointment) => {
    // Certifique-se de inicializar todas as propriedades necessárias
    setEditingAppointment({
      ...appointment,
      originalDate: appointment.date, // Mantém um registro da data original para comparação posterior
      originalHora: appointment.hora, // Mantém um registro da hora original para comparação posterior
    });
    fetchAppointmentsForDate(appointment.date, appointment.professional);
    setShowModal(true);
  };
  // Função para fechar o modal
  const handleModalClose = () => {
    setShowModal(false);
    setEditingAppointment({});
  };
  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    const dayOfWeek = new Date(newDate).getDay();
    // Verificar se o dia da semana está mapeado em businessHours
    if (!businessHours[dayOfWeek] || businessHours[dayOfWeek].closed) {
      alert("Não há atendimento neste dia. Por favor, escolha outro dia.");
      return;
    }
    const professional = editingAppointment.professional;
    setEditingAppointment(prev => ({ ...prev, date: newDate }));
    await fetchAppointmentsForDate(newDate, professional);
  };
  const handleTimeChange = (e) => {
    setEditingAppointment(prev => ({ ...prev, hora: e.target.value }));
  };
  // Função para atualizar o agendamento
  const handleUpdateAppointment = async () => {
    if (!editingAppointment.date || !editingAppointment.hora) {
      alert("Informações de agendamento incompletas para a atualização.");
      return;
    }
    // Obter a data atual e ajustar para o início do dia
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Converter a data original do agendamento para um objeto Date
    const originalAppointmentDate = new Date(editingAppointment.originalDate);
    // Verificar se a data original do agendamento é hoje
    if (originalAppointmentDate.toISOString().split('T')[0] === today.toISOString().split('T')[0]) {
      alert("Não é possível editar agendamentos no dia atual.");
      return;
    }
    // Verifica se o novo horário está disponível (excluindo o próprio agendamento em caso de mudança)
    const isAvailable = await isTimeSlotAvailable(
      editingAppointment.date,
      editingAppointment.hora,
      editingAppointment.professional
    );
    if (!isAvailable) {
      alert("Este horário não está disponível. Por favor, escolha outro horário.");
      return;
    }
    try {
      // Acessa o documento do agendamento associado ao ID do usuário original
      const appointmentRef = doc(db, "agendamentos", editingAppointment.userId);
      const docSnapshot = await getDoc(appointmentRef);
      if (!docSnapshot.exists()) {
        console.error('Documento de agendamentos não encontrado.');
        return;
      }
      let appointmentsData = docSnapshot.data();
      let originalDate = editingAppointment.originalDate;
      let newAppointmentDate = editingAppointment.date;
      // Remove o agendamento antigo (pela hora original) e insere o editado —
      // tudo numa ÚNICA gravação: evita duplicata ao salvar sem mudar o horário,
      // e o aviso de remarcação (Telegram/WhatsApp) depende de ver o antes e o
      // depois na mesma escrita.
      if (Array.isArray(appointmentsData[originalDate])) {
        appointmentsData[originalDate] = appointmentsData[originalDate].filter(appointment =>
          !(appointment.hora === editingAppointment.originalHora && appointment.professional === editingAppointment.professional)
        );
      }
      if (!Array.isArray(appointmentsData[newAppointmentDate])) {
        appointmentsData[newAppointmentDate] = [];
      }
      appointmentsData[newAppointmentDate].push({
        ...editingAppointment,
        data: newAppointmentDate
      });
      if (originalDate !== newAppointmentDate && appointmentsData[originalDate].length === 0) {
        appointmentsData[originalDate] = deleteField(); // campo vazio some na mesma gravação
      }
      await updateDoc(appointmentRef, appointmentsData);
      alert('Agendamento atualizado com sucesso!');
      setShowModal(false);
      setEditingAppointment({});
      // Recarrega os agendamentos do usuário
      const activeFilter = isAdmin ? undefined : currentProfessionalName;
      fetchAppointmentsForDate(selectedDate, activeFilter);
    } catch (error) {
      console.error('Erro ao editar agendamento:', error);
      alert(`Erro ao editar agendamento. Detalhes: ${error.message || error}`);
    }
  };
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/loginBarber');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  // silent: refresh em segundo plano (tempo real) — não mostra "Carregando..." nem pisca a tabela
  const fetchAppointmentsForDate = async (date, professionalFilter, { silent = false } = {}) => {
    // Registra o alvo desta busca e invalida buscas anteriores ainda em andamento
    lastFetchRef.current = { date, filter: professionalFilter };
    const fetchSeq = ++fetchSeqRef.current;
    if (!silent) {
      setLoading(true);
    }
    const dayOfWeek = new Date(date).getDay();
    const defaultHours = businessHours[dayOfWeek];
    if (!defaultHours || defaultHours.closed) {
      setAvailableTimes([]);
      setLoading(false);
      return;
    }
    try {
      const specialDayRef = doc(db, "specialDays", date);
      const specialDaySnap = await getDoc(specialDayRef);
      let workingHours = defaultHours;
      let reservedTimes = [];
      let shouldBlockDay = false;
      if (specialDaySnap.exists()) {
        const specialDayData = specialDaySnap.data();
        const professionalDetails = professionalFilter && specialDayData.professionals
          ? specialDayData.professionals[professionalFilter]
          : undefined;
        const mergedDetails = {
          status: professionalDetails?.status ?? specialDayData.status,
          open: professionalDetails?.open ?? specialDayData.open,
          close: professionalDetails?.close ?? specialDayData.close,
          times: professionalDetails?.times ?? specialDayData.times,
        };
        const status = mergedDetails.status || 'Normal';
        if (status === "Fechado") {
          if (mergedDetails.open && mergedDetails.close) {
            workingHours = defaultHours;
            for (let h = parseInt(mergedDetails.open); h <= parseInt(mergedDetails.close); h++) {
              reservedTimes.push(`${h.toString().padStart(2, '0')}:00`);
            }
          } else {
            shouldBlockDay = true;
            workingHours = null;
          }
        } else if (status === "Especial" && mergedDetails.open && mergedDetails.close) {
          workingHours = { open: mergedDetails.open, close: mergedDetails.close };
        } else if (status === "Reservado" && Array.isArray(mergedDetails.times)) {
          workingHours = defaultHours;
          reservedTimes = mergedDetails.times.map(time => time.close).filter(time => time);
          if (mergedDetails.close) {
            reservedTimes.push(mergedDetails.close);
          }
        } else {
          workingHours = defaultHours;
        }
      }
      // Usa o cache em tempo real; se ainda não carregou (ou o listener morreu),
      // lê direto do servidor
      let docsMap = appointmentDocsRef.current;
      if (!docsMap || appointmentListenerDeadRef.current) {
        const querySnapshot = await getDocs(collection(db, 'agendamentos'));
        docsMap = new Map();
        querySnapshot.forEach((docSnap) => docsMap.set(docSnap.id, docSnap.data()));
        // Só assume o cache se o listener ainda não entregou nada — nunca
        // sobrescreve um snapshot mais fresco que chegou durante esta leitura
        if (!appointmentListenerDeadRef.current && appointmentDocsRef.current === null) {
          appointmentDocsRef.current = docsMap;
        }
      }
      // Uma busca mais nova começou enquanto esta aguardava a rede: descarta esta
      if (fetchSeq !== fetchSeqRef.current) {
        return;
      }
      const bookedTimes = new Set();
      const shouldFilterByProfessional = Boolean(professionalFilter);
      docsMap.forEach((docData) => {
        const userAppointments = docData[date] || [];
        userAppointments.forEach((appointment) => {
          if (shouldFilterByProfessional) {
            if (appointment.professional === professionalFilter) {
              bookedTimes.add(appointment.hora);
            }
          } else {
            bookedTimes.add(appointment.hora);
          }
        });
      });
      reservedTimes.forEach(time => bookedTimes.add(time));
      const newAvailableTimes = [];
      if (workingHours && workingHours.open && workingHours.close && !shouldBlockDay) {
        for (let hour = parseInt(workingHours.open); hour <= parseInt(workingHours.close); hour++) {
          const formattedHour = `${hour < 10 ? '0' : ''}${hour}:00`;
          if (!bookedTimes.has(formattedHour)) {
            newAvailableTimes.push(formattedHour);
          }
        }
      }
      const dateAppointments = [];
      docsMap.forEach((docData, docId) => {
        const userAppointments = docData[date] || [];
        userAppointments.forEach((appointment) => {
          if (appointment.userId === docId) {
            if (!shouldFilterByProfessional || appointment.professional === professionalFilter) {
              const matchedUser = userDirectory.get(docId);
              dateAppointments.push({
                ...appointment,
                userId: docId,
                userPhone: resolvePhoneNumber(appointment, matchedUser),
              });
            }
          }
        });
      });
      // Ordena os agendamentos por hora em ordem ascendente
      dateAppointments.sort((a, b) => {
        return a.hora.localeCompare(b.hora);
      });
      setAppointments(dateAppointments);
      setAvailableTimes(newAvailableTimes);
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
    }
    setLoading(false);
  };
  const handleManualProfessionalSelect = async (event) => {
    const selectedProfessionalId = event.target.value;
    if (!selectedProfessionalId) {
      setCurrentProfessionalId(null);
      setCurrentProfessionalName(null);
      return;
    }
    const selectedProfessional = professionals.find((professional) => professional.id === selectedProfessionalId);
    if (!selectedProfessional) {
      return;
    }
    const authInstance = getAuth();
    const user = authInstance.currentUser;
    if (!user) {
      return;
    }
    try {
      const ownerDocRef = doc(db, 'propietario', user.uid);
      await setDoc(
        ownerDocRef,
        {
          professionalId: selectedProfessional.id,
          professionalName: selectedProfessional.name,
        },
        { merge: true }
      );
      setCurrentProfessionalId(selectedProfessional.id);
      setCurrentProfessionalName(selectedProfessional.name);
      await fetchAppointmentsForDate(selectedDate, selectedProfessional.name);
    } catch (error) {
      console.error('Erro ao associar profissional ao barbeiro:', error);
      alert('Não foi possível vincular o profissional selecionado. Tente novamente.');
    }
  };
  // Vira true uma única vez, quando os horários de funcionamento chegam do banco
  const businessHoursLoaded = Object.keys(businessHours).length > 0;
  useEffect(() => {
    if (!authReady) {
      return;
    }
    const activeFilter = isAdmin ? undefined : currentProfessionalName;
    if (!isAdmin && !activeFilter) {
      // Sem profissional vinculado: limpa a tela e impede o refresh em tempo real
      // de repopular com o alvo antigo
      lastFetchRef.current = null;
      setAppointments([]);
      setAvailableTimes([]);
      setLoading(false);
      return;
    }
    fetchAppointmentsForDate(selectedDate, activeFilter);
    // businessHoursLoaded: refaz a busca quando os horários chegam (corrige tela vazia ao abrir)
  }, [selectedDate, authReady, isAdmin, currentProfessionalName, businessHoursLoaded]);
  // Atualizações em tempo real: quando o cache recebe mudanças, refaz a ÚLTIMA busca feita
  // (seja o painel, o modal de edição ou o dia especial — preserva a data/profissional em uso)
  // em modo silencioso, sem piscar "Carregando..." na tabela.
  useEffect(() => {
    if (!appointmentDocs || !authReady) {
      return;
    }
    const last = lastFetchRef.current;
    if (!last) {
      return;
    }
    fetchAppointmentsForDate(last.date, last.filter, { silent: true });
  }, [appointmentDocs]);
  useEffect(() => {
    const fetchServices = async () => {
      const servicesCollection = collection(db, 'services');
      const servicesSnapshot = await getDocs(servicesCollection);
      const servicesList = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServices(servicesList);
    };
    fetchServices();
  }, []); // Dependências vazias significam que isso será executado uma vez quando o componente for montado
  useEffect(() => {
    if (showBarbers) {
      loadProfessionals();
    }
  }, [showBarbers, loadProfessionals]);
  // Ao abrir a aba Barbeiros: descobre o username do bot do Telegram (uma vez por sessão)
  useEffect(() => {
    if (!showBarbers || telegramBotInfoRequestedRef.current) {
      return;
    }
    telegramBotInfoRequestedRef.current = true;
    const getTelegramBotInfo = httpsCallable(functions, 'getTelegramBotInfo');
    getTelegramBotInfo()
      .then((result) => {
        const username = result?.data?.username;
        setTelegramBotUsername(typeof username === 'string' && username.trim() ? username.trim() : null);
        setNotifChannels({
          telegram: Boolean(result?.data?.telegramEnabled),
          whatsapp: Boolean(result?.data?.whatsappEnabled),
        });
      })
      .catch((error) => {
        console.error('Erro ao consultar o bot do Telegram:', error);
        // Sem resposta: mantém desconhecido, a UI mostra "bot não configurado"
        telegramBotInfoRequestedRef.current = false;
      });
  }, [showBarbers]);
  // Ao abrir a aba Barbeiros (somente admin): carrega os avisos do dono
  useEffect(() => {
    if (!showBarbers || !isAdmin) {
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'configuracoes', 'notificacoes'))
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        const data = snapshot.exists() ? snapshot.data() : {};
        setNotifConfig({
          ownerWhatsapp: data?.ownerWhatsapp || '',
          ownerTelegramChatId: data?.ownerTelegramChatId ? String(data.ownerTelegramChatId) : '',
          events: { ...DEFAULT_NOTIFICATION_EVENTS, ...(data?.events || {}) },
        });
      })
      .catch((error) => {
        console.error('Erro ao carregar avisos do dono:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [showBarbers, isAdmin]);
  const handleNotifEventToggle = (eventKey, checked) => {
    setNotifConfig((prev) => ({
      ...prev,
      events: { ...prev.events, [eventKey]: checked },
    }));
  };
  const handleSaveNotifConfig = async () => {
    setSavingNotifConfig(true);
    try {
      await setDoc(
        doc(db, 'configuracoes', 'notificacoes'),
        {
          ownerWhatsapp: (notifConfig.ownerWhatsapp || '').trim(),
          events: { ...DEFAULT_NOTIFICATION_EVENTS, ...notifConfig.events },
        },
        { merge: true }
      );
      alert('Avisos salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar avisos do dono:', error);
      alert('Não foi possível salvar os avisos.');
    } finally {
      setSavingNotifConfig(false);
    }
  };
  // Liga/desliga o WhatsApp de um profissional (profissionais/{id}.notifWhatsapp, padrão true)
  const handleToggleProfessionalWhatsapp = async (barber, checked) => {
    if (!barber?.id) return;
    try {
      await updateDoc(doc(db, 'profissionais', barber.id), { notifWhatsapp: checked });
      setProfessionals((prev) =>
        prev.map((professional) =>
          professional.id === barber.id ? { ...professional, notifWhatsapp: checked } : professional
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar aviso por WhatsApp do profissional:', error);
      alert('Não foi possível atualizar o aviso por WhatsApp.');
    }
  };
  // Conectar Telegram: o servidor gera um link com código de USO ÚNICO (24 h);
  // sem esse código ninguém consegue se conectar aos avisos
  const [connectingTelegram, setConnectingTelegram] = useState(null);
  const [telegramLinks, setTelegramLinks] = useState({}); // chave -> link gerado
  const handleConnectTelegram = async (target, professionalId = null) => {
    const key = target === 'owner' ? 'owner' : professionalId;
    setConnectingTelegram(key);
    try {
      const createTelegramLinkToken = httpsCallable(functions, 'createTelegramLinkToken');
      const result = await createTelegramLinkToken({ target, professionalId });
      const deepLink = result?.data?.deepLink;
      if (!deepLink) throw new Error('Link não gerado');
      setTelegramLinks((prev) => ({ ...prev, [key]: deepLink }));
    } catch (error) {
      console.error('Erro ao gerar link do Telegram:', error);
      alert(error?.message || 'Não foi possível gerar o link do Telegram.');
    } finally {
      setConnectingTelegram(null);
    }
  };
  // Texto de status de um canal de envio
  const channelLabel = (enabled, pendingHint) =>
    enabled === null ? 'verificando…' : enabled ? 'ligado ✅' : `desligado (${pendingHint})`;
  const renderTelegramConnect = (key, target, professionalId, connected) => {
    if (connected) return 'Telegram ✅';
    if (!telegramBotUsername) return 'Telegram: bot não configurado';
    if (telegramLinks[key]) {
      return (
        <div>
          <a href={telegramLinks[key]} target="_blank" rel="noopener noreferrer">
            Abrir Telegram ↗ (link de uso único, válido por 24 h)
          </a>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Depois de conectar, reabra a aba Barbeiros para ver o ✅.{' '}
            <button
              type="button"
              className="edit-button"
              onClick={() => handleConnectTelegram(target, professionalId)}
              disabled={connectingTelegram === key}
            >
              {connectingTelegram === key ? 'Gerando...' : 'Gerar novo link'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="edit-button"
        onClick={() => handleConnectTelegram(target, professionalId)}
        disabled={connectingTelegram === key}
      >
        {connectingTelegram === key ? 'Gerando link...' : 'Conectar Telegram'}
      </button>
    );
  };
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showClientsDashboard, setShowClientsDashboard] = useState(false);
  const handleOpenServicesModal = () => {
    setShowServicesModal(true);
    setShowAppointments(false);
    setShowSchedule(false);
    setShowUsers(false);
    setShowBarbers(false);
    setShowClientsDashboard(false);
  };
  const handleShowAppointments = () => {
    setShowAppointments(true);
    setShowServicesModal(false);
    setShowSchedule(false);
    setShowUsers(false);
    setShowBarbers(false);
    setShowClientsDashboard(false);
  };
  const handleShowSchedule = () => {
    setShowSchedule(true);
    setShowAppointments(false);
    setShowServicesModal(false);
    setShowUsers(false);
    setShowBarbers(false);
    setShowClientsDashboard(false);
  };
  const handleShowUsers = () => {
    setShowUsers(true);
    setShowAppointments(false);
    setShowServicesModal(false);
    setShowSchedule(false);
    setShowBarbers(false);
    setShowClientsDashboard(false);
    fetchUsers(); // Carrega os usuários quando a seção é exibida
  };
  const handleShowBarbers = () => {
    setShowBarbers(true);
    setShowAppointments(false);
    setShowServicesModal(false);
    setShowSchedule(false);
    setShowUsers(false);
    setShowClientsDashboard(false);
  };
  const handleShowClientsDashboard = () => {
    setShowClientsDashboard(true);
    setShowBarbers(false);
    setShowAppointments(false);
    setShowUsers(false);
    setShowServicesModal(false);
    setShowSchedule(false);
  };
  const handleBarberInputChange = (e) => {
    const { name, value } = e.target;
    setBarberForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleSaveBarber = async (e) => {
    e.preventDefault();
    const trimmedName = barberForm.name.trim();
    if (!trimmedName) {
      alert('Informe o nome do barbeiro.');
      return;
    }
    try {
      if (editingBarberId) {
        await updateDoc(doc(db, 'profissionais', editingBarberId), {
          name: trimmedName,
          email: barberForm.email.trim(),
          phone: barberForm.phone.trim(),
        });
      } else {
        await addDoc(collection(db, 'profissionais'), {
          name: trimmedName,
          email: barberForm.email.trim(),
          phone: barberForm.phone.trim(),
        });
      }
      const refreshedProfessionals = await loadProfessionals();
      if (!editingBarberId && refreshedProfessionals.length === 1) {
        setCurrentProfessionalId(refreshedProfessionals[0].id || null);
        setCurrentProfessionalName(refreshedProfessionals[0].name || null);
      }
      setBarberForm({ name: '', email: '', phone: '' });
      setEditingBarberId(null);
      alert('Barbeiro salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar barbeiro:', error);
      alert('Não foi possível salvar o barbeiro.');
    }
  };
  const handleEditBarber = (barber) => {
    setBarberForm({
      name: barber.name || '',
      email: barber.email || '',
      phone: barber.phone || barber.contactPhone || '',
    });
    setEditingBarberId(barber.id || null);
  };
  const handleDeleteBarber = async (barberId) => {
    if (!barberId) return;
    const confirmDelete = window.confirm('Deseja realmente excluir este barbeiro?');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'profissionais', barberId));
      await loadProfessionals();
      if (editingBarberId === barberId) {
        setEditingBarberId(null);
        setBarberForm({ name: '', email: '', phone: '' });
      }
    } catch (error) {
      console.error('Erro ao excluir barbeiro:', error);
      alert('Não foi possível excluir o barbeiro.');
    }
  };
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableService, setEditableService] = useState(null);
  const [barberForm, setBarberForm] = useState({ name: '', email: '', phone: '' });
  const [editingBarberId, setEditingBarberId] = useState(null);
  // Função para manipular a edição de um serviço
  // Abre o modal de edição e define o serviço atual como alvo da edição
  const handleEditService = (service) => {
    setEditableService(service); // define o serviço a ser editado
    setIsEditModalOpen(true); // abre o modal
  };
  // Função para atualizar um serviço no Firebase
  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editableService || !editableService.id) return; // Verifica se um serviço foi realmente selecionado
    // Referência do documento do serviço específico
    const serviceRef = doc(db, "services", editableService.id);
    try {
      // Atualiza o documento com os novos dados
      await updateDoc(serviceRef, {
        name: editableService.name,
        price: Number(editableService.price),
      });
      // Atualiza a lista de serviços na interface do usuário
      setServices(services.map((service) => (service.id === editableService.id ? editableService : service)));
      // Fecha o modal
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Erro ao atualizar o serviço:", error);
      // Trate o erro conforme necessário
    }
  };
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    price: ''
  });
  const handleAddService = () => {
    setIsAddModalOpen(true);
  };
  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      // Validação básica dos dados do novo serviço
      if (!newService.name || !newService.price) {
        console.error('Por favor, preencha todos os campos.');
        return;
      }
      // Criação do novo serviço no Firebase
      const docRef = await addDoc(collection(db, "services"), {
        name: newService.name,
        price: Number(newService.price)
      });
      console.log("Novo serviço criado com ID: ", docRef.id);
      // Fecha o modal após a criação do serviço
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Erro ao criar o serviço:", error);
      // Trate o erro conforme necessário
    }
  };
  // Função para manipular a exclusão de um serviço
  const handleDeleteService = async (serviceId) => {
    const productRef = doc(db, "services", serviceId); // Referência do documento do produto específico
    try {
      await deleteDoc(productRef); // Deleta o produto do Firebase
      setServices(services.filter((service) => service.id !== serviceId)); // Remove o produto da lista local
    } catch (error) {
      console.error("Erro ao deletar o serviço:", error);
      // Trate o erro conforme necessário
    }
  };
  useEffect(() => {
    const fetchProducts = async () => {
      const productsCollection = collection(db, 'products');
      const productsSnapshot = await getDocs(productsCollection);
      const productsList = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsList);
    };
    fetchProducts();
  }, []); // Dependências vazias significam que isso será executado uma vez quando o componente for montado
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [editableProduct, setEditableProduct] = useState(null);
  const handleEditProduct = (product) => {
    setEditableProduct(product); // define o produto a ser editado
    setIsEditProductModalOpen(true); // abre o modal de edição
  };
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editableProduct || !editableProduct.id) return; // Verifica se um produto foi realmente selecionado
    const productRef = doc(db, "products", editableProduct.id); // Referência do documento do produto específico
    try {
      await updateDoc(productRef, {
        name: editableProduct.name,
        price: editableProduct.price,
      });
      setProducts(products.map((product) => (product.id === editableProduct.id ? editableProduct : product))); // Atualiza lista local
      setIsEditProductModalOpen(false); // Fecha o modal
    } catch (error) {
      console.error("Erro ao atualizar o produto:", error);
      // Trate o erro conforme necessário
    }
  };
  const handleDeleteProduct = async (productId) => {
    const productRef = doc(db, "products", productId); // Referência do documento do produto específico
    try {
      await deleteDoc(productRef); // Deleta o produto do Firebase
      setProducts(products.filter((product) => product.id !== productId)); // Remove o produto da lista local
    } catch (error) {
      console.error("Erro ao deletar o produto:", error);
      // Trate o erro conforme necessário
    }
  };
  const [editableHours, setEditableHours] = useState(null);
  const [isEditHoursModalOpen, setIsEditHoursModalOpen] = useState(false);
  const [closeMonthValue, setCloseMonthValue] = useState('');

  const handleToggleWeekdayClosed = async (day, hours) => {
    const newClosed = !hours.closed;
    const label = hours.semana || `Dia ${day}`;
    const confirmMsg = newClosed
      ? `Fechar "${label}" permanentemente para agendamentos?`
      : `Reabrir "${label}" para agendamentos?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const hourRef = doc(db, 'businessHours', hours.id);
      await updateDoc(hourRef, { closed: newClosed });
      setBusinessHours(prev => ({
        ...prev,
        [day]: { ...prev[day], closed: newClosed },
      }));
      alert(`"${label}" ${newClosed ? 'fechado' : 'aberto'} com sucesso!`);
    } catch (error) {
      console.error('Erro ao alterar dia:', error);
      alert('Ocorreu um erro ao alterar o dia.');
    }
  };

  const handleCloseEntireMonth = async () => {
    if (!closeMonthValue) {
      alert('Selecione um mês.');
      return;
    }
    const [year, month] = closeMonthValue.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    if (!window.confirm(`Fechar todos os ${daysInMonth} dias de ${closeMonthValue}?`)) return;
    try {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        await setDoc(doc(db, 'specialDays', date), { status: 'Fechado' }, { merge: true });
      }
      alert(`Todos os dias de ${closeMonthValue} foram fechados com sucesso!`);
    } catch (error) {
      console.error('Erro ao fechar mês:', error);
      alert('Ocorreu um erro ao fechar o mês.');
    }
  };

  const handleOpenEntireMonth = async () => {
    if (!closeMonthValue) {
      alert('Selecione um mês.');
      return;
    }
    const [year, month] = closeMonthValue.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    if (!window.confirm(`Reabrir todos os ${daysInMonth} dias de ${closeMonthValue}?`)) return;
    try {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        await setDoc(doc(db, 'specialDays', date), { status: 'Normal' }, { merge: true });
      }
      alert(`Todos os dias de ${closeMonthValue} foram reabertos com sucesso!`);
    } catch (error) {
      console.error('Erro ao reabrir mês:', error);
      alert('Ocorreu um erro ao reabrir o mês.');
    }
  };
  // Função chamada quando o botão editar é clicado
  const handleEditHours = (day, hours) => {
    setEditableHours({ ...hours, day }); // Incluir o ID aqui
    setIsEditHoursModalOpen(true);
  };
  const handleUpdateBusinessHours = async (e) => {
    e.preventDefault();
    if (!editableHours || !editableHours.day) return;
    try {
      // Usando o ID do documento para referenciar o horário específico
      const hourRef = doc(db, "businessHours", editableHours.id);
      await updateDoc(hourRef, {
        open: editableHours.open,
        close: editableHours.close,
      });
      // Atualiza a interface do usuário com os novos horários
      setBusinessHours({
        ...businessHours,
        [editableHours.day]: { ...editableHours, open: editableHours.open, close: editableHours.close }
      });
      setIsEditHoursModalOpen(false);
      alert("Horários atualizados com sucesso!");
    } catch (error) {
      console.error("Erro ao atualizar horários:", error);
      alert("Ocorreu um erro ao atualizar os horários.");
    }
  };
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const buildDefaultDayDetails = (date) => {
    const dayOfWeek = new Date(date).getDay();
    const defaultHours = businessHours[dayOfWeek] || {};
    return {
      date,
      status: 'Normal',
      open: defaultHours.open || '',
      close: defaultHours.close || '',
      times: [],
    };
  };
  const loadSpecialDayDetails = async (date, professionalId) => {
    const docRef = doc(db, "specialDays", date);
    const docSnap = await getDoc(docRef);
    const isGeneral = professionalId === GENERAL_SPECIAL_DAY_PROFESSIONAL;
    let details = buildDefaultDayDetails(date);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const professionalDetails = !isGeneral && data.professionals
        ? data.professionals[professionalId]
        : undefined;
      const mergedDetails = {
        status: professionalDetails?.status ?? data.status ?? details.status,
        open: professionalDetails?.open ?? data.open ?? details.open,
        close: professionalDetails?.close ?? data.close ?? details.close,
        times: professionalDetails?.times ?? data.times ?? [],
      };
      details = { ...details, ...mergedDetails };
    }
    const normalizedTimes = details.status === 'Reservado' && Array.isArray(details.times) && details.times.length > 0
      ? details.times.map(time => ({ ...time }))
      : [{ open: '', close: '' }];
    setTimes(normalizedTimes);
    setSelectedDayDetails({
      ...details,
      times: details.status === 'Reservado' ? normalizedTimes : [],
      professionalId: isGeneral ? null : professionalId,
    });
  };
  const handleSpecialDayProfessionalChange = async (e) => {
    const professionalId = e.target.value;
    setSpecialDayProfessional(professionalId);
    if (selectedDayDetails?.date) {
      await loadSpecialDayDetails(selectedDayDetails.date, professionalId);
      const filter = professionalId === GENERAL_SPECIAL_DAY_PROFESSIONAL ? undefined : professionalId;
      await fetchAppointmentsForDate(selectedDayDetails.date, filter);
    }
  };
  // Função para lidar com a seleção de um dia
  const handleDaySelection = async (e) => {
    const date = e.target.value; // A data selecionada no formato 'yyyy-mm-dd'
    await loadSpecialDayDetails(date, specialDayProfessional);
    const filter = specialDayProfessional === GENERAL_SPECIAL_DAY_PROFESSIONAL ? undefined : specialDayProfessional;
    await fetchAppointmentsForDate(date, filter);
  };
  // Função para editar os detalhes do dia selecionado
  const handleEditDayDetails = async (details) => {
    if (!details?.date) {
      return;
    }
    const docRef = doc(db, "specialDays", details.date);
    const targetProfessional = specialDayProfessional;
    const isGeneral = targetProfessional === GENERAL_SPECIAL_DAY_PROFESSIONAL;
    const dataToSave = {
      status: details.status,
    };
    if (details.status === 'Reservado') {
      dataToSave.times = Array.isArray(details.times) ? details.times.map(time => ({ ...time })) : [];
      dataToSave.open = '';
      dataToSave.close = details.close || '';
    } else if (details.status === 'Especial') {
      dataToSave.open = details.open || '';
      dataToSave.close = details.close || '';
      dataToSave.times = [];
    } else if (details.status === 'Fechado') {
      dataToSave.open = details.open || '';
      dataToSave.close = details.close || '';
      dataToSave.times = [];
    } else {
      dataToSave.open = details.open || '';
      dataToSave.close = details.close || '';
      dataToSave.times = [];
    }
    if (isGeneral) {
      await setDoc(docRef, dataToSave, { merge: true });
    } else {
      await setDoc(docRef, {
        professionals: {
          [targetProfessional]: dataToSave,
        },
      }, { merge: true });
    }
    const normalizedTimes = details.status === 'Reservado' ? (dataToSave.times || []) : [];
    setSelectedDayDetails({
      ...details,
      ...dataToSave,
      times: normalizedTimes,
      professionalId: isGeneral ? null : targetProfessional,
    });
    setTimes(details.status === 'Reservado' && normalizedTimes.length > 0 ? normalizedTimes : [{ open: '', close: '' }]);
    alert("Detalhes do dia atualizados com sucesso!");
    const filter = isGeneral ? undefined : targetProfessional;
    await fetchAppointmentsForDate(details.date, filter);
  };
  // Estado inicial para armazenar múltiplos horários
  const [times, setTimes] = useState([{ open: '', close: '' }]);
  // Função para adicionar um novo horário
  const addTimeSlot = () => {
    setTimes([...times, { open: '', close: '' }]);
  };
  // Função para atualizar um horário específico
  const updateTimeSlot = (index, field, value) => {
    const newTimes = [...times];
    newTimes[index][field] = value;
    setTimes(newTimes);
  };
  // Função para remover um horário específico
  const removeTimeSlot = (index) => {
    const newTimes = [...times];
    newTimes.splice(index, 1);
    setTimes(newTimes);
  };
  const fetchUsers = async () => {
    const usersCollectionRef = collection(db, "users");
    const usersSnapshot = await getDocs(usersCollectionRef);
    const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUsers(usersList);
  };
  useEffect(() => {
    fetchUsers();
  }, []);
  useEffect(() => {
    setAppointments((previousAppointments) => {
      if (!previousAppointments || previousAppointments.length === 0) {
        return previousAppointments;
      }
      let hasChanges = false;
      const updatedAppointments = previousAppointments.map((appointment) => {
        const matchedUser = appointment?.userId ? userDirectory.get(appointment.userId) : null;
        const resolvedPhone = resolvePhoneNumber(appointment, matchedUser);
        if ((resolvedPhone || '') === (appointment?.userPhone || '')) {
          return appointment;
        }
        hasChanges = true;
        return {
          ...appointment,
          userPhone: resolvedPhone,
        };
      });
      return hasChanges ? updatedAppointments : previousAppointments;
    });
  }, [userDirectory]);
  return (
    <div>
      <nav className="navbarBarbe">
        <img src={LogoBarberia} alt="Logo" className="logo" />
        <button className="logout-button" onClick={handleLogout}>Sair</button>
      </nav>
      <aside className={`sidebar ${isSidebarOpen ? '' : 'closed'}`}>
        <button className="toggle-button" onClick={toggleSidebar}>
          {isSidebarOpen ? <FaArrowLeft /> : <FaBars />}
        </button>
        <ul className="sidebar-nav">
          <li onClick={handleShowAppointments}>
            {isSidebarOpen ? 'Agendados' : <FaCalendarAlt />}
          </li>
          <li onClick={handleShowClientsDashboard}>
            {isSidebarOpen ? 'Dashboard Clientes' : <FaChartPie />}
          </li>
          <li onClick={handleOpenServicesModal}>
            {isSidebarOpen ? 'Meus Serviços' : <FaTools />}
          </li>
          <li onClick={handleShowSchedule}>
            {isSidebarOpen ? 'Horários' : <FaClock />}
          </li>
          <li onClick={handleShowUsers}>
            {isSidebarOpen ? 'Usuários' : <FaUsers />}
          </li>
          <li onClick={handleShowBarbers}>
            {isSidebarOpen ? 'Barbeiros' : <FaUsers />}
          </li>
        </ul>
      </aside>
      <main>
        {!isAdmin && authReady && (
          <div className="professional-banner">
            {currentProfessionalName ? (
              <p>
                Atendendo como: <strong>{currentProfessionalName}</strong>
              </p>
            ) : (
              <div className="professional-assignment">
                <p>Selecione o seu perfil de profissional para visualizar seus agendamentos.</p>
                <select
                  value={currentProfessionalId || ''}
                  onChange={handleManualProfessionalSelect}
                  disabled={professionals.length === 0}
                >
                  <option value="">Escolha um profissional</option>
                  {professionals.map((professional) => (
                    <option key={professional.id} value={professional.id}>
                      {professional.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        {showClientsDashboard && <DashboardClientes users={users} />}
        {/* Modal para mostrar os serviços */}
        {showServicesModal && (
          <div className="services-modal">
            <h2>Meus Serviços</h2>
            <button className="add-button" onClick={handleAddService}>Adicionar Serviço</button>
            <table className="servicos-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Preço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {services.map(service => (
                  <tr key={service.id}>
                    <td>{service.name}</td>
                    <td>{service.price}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEditService(service)}>Editar</button>
                      <button className="delete-button" onClick={() => handleDeleteService(service.id)}>Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h2>Produtos</h2>
            <table className="produtos-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Preço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>{product.price}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEditProduct(product)}>Editar</button>
                      <button className="delete-button" onClick={() => handleDeleteProduct(product.id)}>Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isAddModalOpen && (
              <div className="add-modal-overlay">
                <div className="add-modal-container">
                  <span className="add-modal-close" onClick={() => setIsAddModalOpen(false)}>&times;</span>
                  <h2 className="add-modal-title">Adicionar Serviço</h2>
                  <label className="add-modal-label">Nome do Serviço:</label>
                  <input
                    className="add-modal-input"
                    type="text"
                    value={newService.name}
                    onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  />
                  <label className="add-modal-label">Preço:</label>
                  <input
                    className="add-modal-input"
                    type="text" // ou 'number' se preferir que só sejam inseridos números
                    value={newService.price}
                    onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                  />
                  <button className="add-modal-button" onClick={handleCreateService}>Adicionar Serviço</button>
                </div>
              </div>
            )}
          </div>
        )}
        {isEditModalOpen && (
          <div className="edit-modal-overlay">
            <div className="edit-modal-container">
              <span className="edit-modal-close" onClick={() => setIsEditModalOpen(false)}>&times;</span>
              <h2 className="edit-modal-title">Editar Serviço</h2>
              <label className="edit-modal-label">Nome do Serviço:</label>
              <input
                className="edit-modal-input"
                type="text"
                value={editableService.name}
                onChange={(e) => setEditableService({ ...editableService, name: e.target.value })}
              />
              <label className="edit-modal-label">Preço:</label>
              <input
                className="edit-modal-input"
                type="text" // ou 'number' se preferir que só sejam inseridos números
                value={editableService.price}
                onChange={(e) => setEditableService({ ...editableService, price: e.target.value })}
              />
              <button className="edit-modal-button" onClick={handleUpdateService}>Salvar Alterações</button>
            </div>
          </div>
        )}
        {isEditProductModalOpen && (
          <div className="edit-modal-overlay">
            <div className="edit-modal-container">
              <span className="edit-modal-close" onClick={() => setIsEditProductModalOpen(false)}>&times;</span>
              <h2 className="edit-modal-title">Editar Produto</h2>
              <label className="edit-modal-label">Nome do Produto:</label>
              <input
                className="edit-modal-input"
                type="text"
                value={editableProduct.name}
                onChange={(e) => setEditableProduct({ ...editableProduct, name: e.target.value })}
              />
              <label className="edit-modal-label">Preço:</label>
              <input
                className="edit-modal-input"
                type="text" // ou 'number' se preferir que só sejam inseridos números
                value={editableProduct.price}
                onChange={(e) => setEditableProduct({ ...editableProduct, price: e.target.value })}
              />
              <button className="edit-modal-button" onClick={handleUpdateProduct}>Salvar Alterações</button>
            </div>
          </div>
        )}
        {showSchedule && (
          <div className="schedule-section">
            <h2>Horários de Negócio</h2>
            <table className="business-hours-table">
              <thead>
                <tr>
                  <th>Dia</th>
                  <th>Abertura</th>
                  <th>Fechamento</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(businessHours).map(([day, hours]) => (
                  <tr key={day} style={hours.closed ? { opacity: 0.5 } : {}}>
                    <td>{hours.semana}</td>
                    <td>{hours.closed ? '—' : hours.open}</td>
                    <td>{hours.closed ? '—' : hours.close}</td>
                    <td>{hours.closed ? 'Fechado' : 'Aberto'}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEditHours(day, hours)} disabled={hours.closed}>Editar</button>
                      <button
                        className={hours.closed ? 'edit-button' : 'delete-button'}
                        onClick={() => handleToggleWeekdayClosed(day, hours)}
                      >
                        {hours.closed ? 'Reabrir' : 'Fechar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="special-days-control" style={{ marginTop: '24px' }}>
              <h3>Fechar / Reabrir Mês Inteiro</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="month"
                  value={closeMonthValue}
                  onChange={(e) => setCloseMonthValue(e.target.value)}
                  className="date-picker"
                />
                <button className="delete-button" onClick={handleCloseEntireMonth}>Fechar mês</button>
                <button className="edit-button" onClick={handleOpenEntireMonth}>Reabrir mês</button>
              </div>
            </div>
            {/* Seção de Controle de Dias Específicos */}
            <div className="special-days-control">
              <h3>Controle de Dias Específicos</h3>
              <input type="date" onChange={handleDaySelection} className="date-picker" />
              <div className="special-day-professional">
                <label>Profissional:</label>
                <select value={specialDayProfessional} onChange={handleSpecialDayProfessionalChange}>
                  <option value={GENERAL_SPECIAL_DAY_PROFESSIONAL}>Configuração Geral</option>
                  {professionals.map((professional) => (
                    <option key={professional.id || professional.name} value={professional.name}>
                      {professional.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedDayDetails && (
                <div className="day-details">
                  <p>Data: <input type="text" value={selectedDayDetails.date} readOnly /></p>
                  <p>Status:
                    <select
                      value={selectedDayDetails.status || 'Normal'}
                      onChange={(e) => {
                        const status = e.target.value;
                        setSelectedDayDetails(prevState => {
                          let formattedOpen = prevState.open;
                          let formattedClose = prevState.close;
                          if (status === 'Especial') {
                            // Ajusta o horário para ser no formato HH:mm com minutos como '00'
                            formattedOpen = prevState.open.split(':')[0] + ':00';
                            formattedClose = prevState.close.split(':')[0] + ':00';
                          } else if (status === 'Reservado') {
                            // Desativa o campo de abertura e permite adicionar um horário extra
                            formattedOpen = ''; // Campo de abertura fica vazio e inativo
                          }
                          // Garante que os horários estejam no formato correto 'HH:mm'
                          formattedOpen = formattedOpen.length === 5 ? formattedOpen : '00:00';
                          formattedClose = formattedClose.length === 5 ? formattedClose : '00:00';
                          return {
                            ...prevState,
                            status: status,
                            open: formattedOpen,
                            close: formattedClose,
                          };
                        });
                      }}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Fechado">Fechado</option>
                      <option value="Especial">Especial</option>
                      <option value="Reservado">Reservado</option>
                    </select>
                  </p>
                  <p>Horário de Abertura:
                    <select
                      type="time"
                      value={selectedDayDetails.open}
                      onChange={(e) => setSelectedDayDetails(prevState => ({ ...prevState, open: e.target.value }))}
                      disabled={selectedDayDetails.status === 'Reservado'}
                    >
                      <option value="" disabled>Selecione um horário</option>
                      {fullDayTimeOptions.map((hour) => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </select>
                  </p>
                  <p>Horário de Fechamento:
                    <select
                      type="time"
                      value={selectedDayDetails.close}
                      onChange={(e) => setSelectedDayDetails(prevState => ({ ...prevState, close: e.target.value }))}
                    >
                      <option value="" disabled>Selecione um horário</option>
                      {fullDayTimeOptions.map((hour) => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </select>
                  </p>
                  {selectedDayDetails.status === 'Reservado' && times.map((time, index) => (
                    <div key={index}>
                      <p>Horário de Abertura:
                        <select
                          type="time"
                          value={time.open}
                          onChange={(e) => updateTimeSlot(index, 'open', e.target.value)}
                        >
                          <option value="" disabled>Selecione um horário</option>
                          {availableTimes.map((hour) => (
                            <option key={hour} value={hour}>{hour}</option>
                          ))}
                        </select>
                      </p>
                      <p>Horário de Fechamento:
                        <select
                          type="time"
                          value={time.close}
                          onChange={(e) => updateTimeSlot(index, 'close', e.target.value)}
                        >
                          <option value="" disabled>Selecione um horário</option>
                          {availableTimes.map((hour) => (
                            <option key={hour} value={hour}>{hour}</option>
                          ))}
                        </select>
                      </p>
                      {index > 0 && (
                        <button onClick={() => removeTimeSlot(index)}>Remover Horário</button>
                      )}
                    </div>
                  ))}
                  <button onClick={addTimeSlot}>Adicionar Novo Horário</button>
                  <button onClick={() => handleEditDayDetails({ ...selectedDayDetails, times })}>Salvar Detalhes</button>
                </div>
              )}
            </div>
          </div>
        )}
        {isEditHoursModalOpen && (
          <div className="edit-modal-overlay">
            <div className="edit-modal-container">
              <span className="edit-modal-close" onClick={() => setIsEditHoursModalOpen(false)}>&times;</span>
              <h2>Editar Horário</h2>
              <form onSubmit={handleUpdateBusinessHours}>
                <label className="edit-modal-label">Dia:</label>
                <div>{editableHours.semana}</div> {/* Mostra o dia sendo editado */}
                <label className="edit-modal-label">Abertura:</label>
                <input
                  className="edit-modal-input"
                  type="time"
                  value={editableHours.open}
                  onChange={(e) => setEditableHours({ ...editableHours, open: e.target.value })}
                />
                <label className="edit-modal-label">Fechamento:</label>
                <input
                  className="edit-modal-input"
                  type="time"
                  value={editableHours.close}
                  onChange={(e) => setEditableHours({ ...editableHours, close: e.target.value })}
                />
              <button type="submit" className="edit-modal-button">Salvar Alterações</button>
            </form>
          </div>
        </div>
      )}
        {showUsers && (
          <div className="users-list">
            <h2>Usuários Cadastrados</h2>
            <p>Total de Usuários: {users.length}</p>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Telefone</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showBarbers && (
          <div className="barbers-section">
            <h2>Barbeiros</h2>
            <form className="barber-form" onSubmit={handleSaveBarber}>
              <div className="barber-form-row">
                <label>Nome</label>
                <input
                  type="text"
                  name="name"
                  value={barberForm.name}
                  onChange={handleBarberInputChange}
                  required
                />
              </div>
              <div className="barber-form-row">
                <label>E-mail</label>
                <input
                  type="email"
                  name="email"
                  value={barberForm.email}
                  onChange={handleBarberInputChange}
                />
              </div>
              <div className="barber-form-row">
                <label>Telefone</label>
                <input
                  type="text"
                  name="phone"
                  value={barberForm.phone}
                  onChange={handleBarberInputChange}
                />
              </div>
              <button type="submit" className="edit-button">
                {editingBarberId ? 'Atualizar Barbeiro' : 'Adicionar Barbeiro'}
              </button>
              {editingBarberId && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={() => {
                    setEditingBarberId(null);
                    setBarberForm({ name: '', email: '', phone: '' });
                  }}
                >
                  Cancelar edição
                </button>
              )}
            </form>
            {isAdmin && (
              <div className="special-days-control owner-notifications">
                <h3>Avisos para o dono</h3>
                <p style={{ fontSize: '13px', opacity: 0.85, margin: '0 0 10px' }}>
                  Canais de envio — WhatsApp: {channelLabel(notifChannels.whatsapp, 'aguardando as credenciais da Meta')} · Telegram: {channelLabel(notifChannels.telegram, 'aguardando o token do bot')}
                </p>
                <div style={{ display: 'flex', gap: '12px 20px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {NOTIFICATION_EVENT_LABELS.map((event) => (
                    <label key={event.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifConfig.events[event.key] !== false}
                        onChange={(e) => handleNotifEventToggle(event.key, e.target.checked)}
                      />
                      {event.label}
                    </label>
                  ))}
                </div>
                <div className="barber-form">
                  <div className="barber-form-row">
                    <label>WhatsApp do dono</label>
                    <input
                      type="text"
                      name="ownerWhatsapp"
                      placeholder="(11) 98765-4321"
                      value={notifConfig.ownerWhatsapp}
                      onChange={(e) => setNotifConfig((prev) => ({ ...prev, ownerWhatsapp: e.target.value }))}
                    />
                  </div>
                  <div className="barber-form-row">
                    <label>Telegram do dono</label>
                    <div>
                      {renderTelegramConnect('owner', 'owner', null, Boolean(notifConfig.ownerTelegramChatId))}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="edit-button"
                  onClick={handleSaveNotifConfig}
                  disabled={savingNotifConfig}
                >
                  {savingNotifConfig ? 'Salvando...' : 'Salvar avisos'}
                </button>
              </div>
            )}
            <table className="barbers-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>Notificações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {professionals.map((barber) => (
                  <tr key={barber.id}>
                    <td>{barber.name}</td>
                    <td>{barber.email || '-'}</td>
                    <td>{barber.phone || '-'}</td>
                    <td>
                      <div>
                        {renderTelegramConnect(barber.id, 'professional', barber.id, Boolean(barber.telegramChatId))}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginTop: '6px' }}>
                        <input
                          type="checkbox"
                          checked={barber.notifWhatsapp !== false}
                          onChange={(e) => handleToggleProfessionalWhatsapp(barber, e.target.checked)}
                        />
                        WhatsApp{notifChannels.whatsapp === false ? ' (canal desligado)' : ''}
                      </label>
                    </td>
                    <td>
                      <button className="edit-button" type="button" onClick={() => handleEditBarber(barber)}>
                        Editar
                      </button>
                      <button className="delete-button" type="button" onClick={() => handleDeleteBarber(barber.id)}>
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Seção para mostrar os agendamentos */}
        {showAppointments && (
          <div className="agendamentos-container">
            <div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={!isAdmin && !currentProfessionalName}
              />
            </div>
            {!isAdmin && !currentProfessionalName && (
              <p className="professional-warning">
                Vincule sua conta a um profissional para carregar os agendamentos.
              </p>
            )}
            {loading ? <p>Carregando...</p> : (
              <div>
                <h2>Agendamentos para {selectedDate}</h2>
                <table className="agendamentos-table">
                  <thead className='head-table'>
                    <tr>
                      <th className='th-cliente'>Cliente</th>
                      <th>Hora</th>
                      <th>Profissional</th>
                      <th>Serviço</th>
                      <th>Produto</th>
                      <th>Pagamento</th>
                      <th>Telefone</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.length > 0 ? (
                      appointments.map((appointment, index) => (
                        <tr key={index}>
                          <td>{appointment.userName}</td>
                          <td>{appointment.hora}</td>
                          <td>{appointment.professional}</td>
                          <td>{Array.isArray(appointment.services) ? appointment.services.join(', ') : appointment.services}</td>
                          <td>{Array.isArray(appointment.products) ? appointment.products.join(', ') : appointment.products}</td>
                          <td>{appointment.paymentStatus}</td>
                          <td>{appointment.userPhone || '-'}</td>
                          <td>
                            <button onClick={() => handleEditAppointment(appointment)}>
                              <FaEdit />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8">Nenhum agendamento para esta data.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {showModal && (
          <div className="edit-modal-overlay">
            <div className="edit-modal-container">
              <span className="edit-modal-close" onClick={handleModalClose}>&times;</span>
              <h2 className="edit-modal-title">Editar Agendamento</h2>
              <label className="edit-modal-label">Data:</label>
              <input className="edit-modal-input" type="date" min={new Date().toISOString().split('T')[0]} value={editingAppointment.date} onChange={handleDateChange} />
              <label className="edit-modal-label">Horário:</label>
              <select className="edit-modal-input" value={editingAppointment.hora} onChange={handleTimeChange}>
                {availableTimes.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              <button className="edit-modal-button" onClick={handleUpdateAppointment}>Salvar Alterações</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}